import React, { memo, useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Box, Card, Theme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ComparisonValue,
  PHONE_CHART_HEIGHT,
  SelectCompare,
} from "@components";
import { CompactRangeButton } from "@components/Chart/ChartControls";
import { NonProContainer } from "@features/dashboard/components/NonProContainer";
import { RangeSelector, performanceIntervalTypes } from "@rentablo/components";
import { Tabs, Tab } from "@rentablo/components";
import {
  DividendsChart,
  PerformanceChart,
  ValuationChart,
} from "@components/Chart/highcharts";
import { useDividendsChartData } from "@api/v1/hooks/performance/useDividendsChartData";
import { useInvestmentsChartDataTotal } from "@api/v1/hooks/performance/useInvestmentsChartDataTotal";
import { useRangeSelectControl } from "@hooks";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { PerformanceChartTypes } from "./types";

const DashboardPerformanceWrapper = styled(Card)`
  padding: ${({ theme }) => theme.spacing(4, 4, 5)};
  min-height: 236px;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TabPanel = styled.div`
  padding-top: ${({ theme }) => theme.spacing(4)};
`;

const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "isCompact",
})<{ centered?: boolean; isCompact: boolean }>(
  ({ theme, centered, isCompact }) => ({
    ".MuiTabs-flexContainer": {
      justifyContent: isCompact
        ? "flex-start"
        : centered
          ? "center"
          : "space-between",
    },
    ...(isCompact && {
      ".MuiTab-root": {
        minWidth: 0,
        padding: theme.spacing(3.5, 2),
      },
    }),
  })
);

const DIVIDEND_DISABLED_INTERVALS: IntervalType[] = ["oneWeek", "oneMonth"];

interface Props {
  showCompare?: boolean;
  showDividendsToggle?: boolean;
  showCapitalToggle?: boolean;
  centered?: boolean;
  chartHeight?: number;
}

export const DashboardPerformance = memo(
  ({
    showCompare,
    showDividendsToggle,
    showCapitalToggle,
    centered = true,
    chartHeight,
  }: Props) => {
    const { t } = useTranslation();
    const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
    const { groupedAccountIds, isFetching: areAccountsLoading } =
      useDashboardSelectedGroupsContext();
    const rangeProps = useRangeSelectControl({
      firstDate: new Date(),
      id: "dashboard",
    });
    const { range, intervalType, selectInterval, customIntervalChange } =
      rangeProps;

    const [displayedChart, setDisplayedChart] =
      useState<PerformanceChartTypes>("valuation");
    const [comparedTicker, setComparedTicker] = useState<ComparisonValue>();

    const accountIds = useMemo(
      () => [
        ...groupedAccountIds.cashIds,
        ...groupedAccountIds.creditCardIds,
        ...groupedAccountIds.depotIds,
      ],
      [groupedAccountIds]
    );

    const {
      data: chartData,
      currency,
      isFetching: isLoadingChartData,
      isFetched: isChartDataFetched,
    } = useInvestmentsChartDataTotal({
      accountIds,
      startDate: range.startDate,
      endDate: range.endDate,
      chartType: displayedChart,
      comparedTicker:
        displayedChart === "performance" ? comparedTicker : undefined,
      areAccountsLoading,
    });

    const {
      chartData: dividendsChartData,
      isFetching: isDividendsLoading,
      isFetched: isDividendsFetched,
    } = useDividendsChartData({
      accountIds,
      startDate: range?.startDate!,
      endDate: range?.endDate,
      interval: intervalType,
      enabled: displayedChart === "dividends",
      displayCurrency: currency,
    });

    const handleTabChange = (tabKey: string) => {
      const isSwitchingToDividends = tabKey === "dividends";
      const isSwitchingAwayFromDividends =
        displayedChart === "dividends" && !isSwitchingToDividends;

      setDisplayedChart(tabKey as PerformanceChartTypes);

      if (
        (intervalType === "oneMonth" || intervalType === "oneWeek") &&
        isSwitchingToDividends
      ) {
        selectInterval("YTD");
      }

      if (
        isSwitchingAwayFromDividends &&
        intervalType === "custom" &&
        range?.endDate
      ) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const end = new Date(range.endDate);
        end.setHours(0, 0, 0, 0);
        if (end > today) {
          customIntervalChange(range.startDate, new Date());
        }
      }
    };

    const isDividends = displayedChart === "dividends";
    const height = isSm ? PHONE_CHART_HEIGHT : chartHeight;

    const renderRangeSelector = useCallback(
      () =>
        isSm ? null : (
          <Box mb={4}>
            <RangeSelector
              size="small"
              range={intervalType === "custom" ? range : undefined}
              onSelect={selectInterval}
              onCustomSelect={customIntervalChange}
              intervalType={intervalType}
              intervalTypes={performanceIntervalTypes}
              allowFutureInCustom={isDividends}
              disabledIntervalTypes={
                isDividends ? DIVIDEND_DISABLED_INTERVALS : []
              }
            />
          </Box>
        ),
      [
        isSm,
        isDividends,
        customIntervalChange,
        intervalType,
        range,
        selectInterval,
      ]
    );

    return (
      <DashboardPerformanceWrapper>
        <StyledTabs
          key={displayedChart}
          onChange={handleTabChange}
          defaultKey={displayedChart}
          centered={centered && !isSm}
          isCompact={isSm}
          buttonElement={
            isSm ? (
              <CompactRangeButton
                rangeProps={rangeProps}
                intervalTypes={performanceIntervalTypes.filter(
                  (type) =>
                    !isDividends || !DIVIDEND_DISABLED_INTERVALS.includes(type)
                )}
                allowFuture={isDividends}
              />
            ) : undefined
          }
        >
          <Tab
            label={t("dashboardPage.dashboard.performance.tabs.value.title")}
            key="valuation"
          >
            <TabPanel>
              {renderRangeSelector()}
              <ValuationChart
                chartData={chartData}
                currency={currency ?? "EUR"}
                isFetching={areAccountsLoading || isLoadingChartData}
                isFetched={isChartDataFetched}
                showInvestedCapitalToggle={showCapitalToggle}
                height={height}
              />
            </TabPanel>
          </Tab>
          <Tab
            label={t(
              "dashboardPage.dashboard.performance.tabs.performance.title"
            )}
            key="performance"
          >
            <TabPanel>
              {renderRangeSelector()}
              <PerformanceChart
                chartData={chartData}
                isFetched={isChartDataFetched}
                isFetching={areAccountsLoading || isLoadingChartData}
                comparedTicker={comparedTicker}
                showDividendsToggle={showDividendsToggle}
                height={height}
              />
            </TabPanel>
          </Tab>
          <Tab
            label={t("dashboardPage.dashboard.performance.tabs.dividend.title")}
            key="dividends"
          >
            <TabPanel>
              {renderRangeSelector()}
              <DividendsChart
                chartData={dividendsChartData}
                interval={intervalType}
                isFetching={areAccountsLoading || isDividendsLoading}
                isFetched={!accountIds?.length ? true : isDividendsFetched}
                height={height}
                currency={currency ?? "EUR"}
              />
            </TabPanel>
          </Tab>
        </StyledTabs>
        <Box>
          <NonProContainer>
            {showCompare && (
              <SelectCompare
                value={comparedTicker}
                onChange={(value?: ComparisonValue) => {
                  setComparedTicker(value);
                  if (value) {
                    handleTabChange("performance");
                  }
                }}
              />
            )}
          </NonProContainer>
        </Box>
      </DashboardPerformanceWrapper>
    );
  }
);
